<div class="main-view space-y-2 md:space-y-4">
    <app-page-surface-header titleIcon="heart" title="Platform health"
        subtitle="Live service and tenant status, uptime and 90-day history. Refreshes every minute.">
        <ng-container ngProjectAs="[pageHeaderActions]">
            <div class="flex flex-wrap items-center gap-2">
                <div class="flex rounded-full border border-gray-200 p-0.5 dark:border-white/10" aria-label="Uptime window">
                    <button *ngFor="let w of windows" type="button"
                        class="rounded-full px-3 py-1 text-xs font-medium"
                        [ngClass]="window() === w ? 'bg-[#303030] text-white' : 'text-gray-500'"
                        (click)="onWindowChange(w)">
                        {{ w }}
                    </button>
                </div>
                <button
                    *ngIf="P.PLATFORM_HEALTH_MANAGE | hasPermission"
                    type="button"
                    class="btn inline-flex items-center gap-2 rounded-full px-6 py-2 text-sm disabled:opacity-50"
                    [ngClass]="
                        checkupEnabled()
                            ? 'bg-[#303030] text-white hover:bg-black'
                            : 'bg-gray-200 text-gray-800 hover:bg-gray-300 dark:bg-white/15 dark:text-gray-100 dark:hover:bg-white/20'
                    "
                    [disabled]="checkupSaving()"
                    (click)="onCheckupToggle(!checkupEnabled())">
                    {{ checkupEnabled() ? 'Monitoring ON' : 'Monitoring OFF' }}
                </button>
                <button type="button"
                    class="btn inline-flex items-center gap-2 rounded-full bg-[#303030] px-6 py-2 text-sm text-white hover:bg-black disabled:opacity-50"
                    [disabled]="!checkupEnabled()"
                    (click)="probeNow()">
                    <icon-refresh class="size-4" />
                    Probe now
                </button>
            </div>
        </ng-container>
    </app-page-surface-header>

    <p *ngIf="!checkupEnabled()" class="rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:bg-amber-500/10 dark:text-amber-200">
        Background health sampling, live probes, and alerts are paused. Historical data below may be stale.
    </p>

    <p *ngIf="checkupError()" class="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700 dark:bg-red-500/10 dark:text-red-300">
        {{ checkupError() }}
    </p>

    <p *ngIf="loadError()" class="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700 dark:bg-red-500/10 dark:text-red-300">
        {{ loadError() }}
    </p>

    <!-- Summary strip: the answer to "is anything wrong?" before the tables. -->
    <div class="grid gap-2 md:grid-cols-3 md:gap-4">
        <div class="panel rounded-2xl p-4">
            <div class="flex items-center gap-1.5 text-xs uppercase text-gray-400"><icon-heart class="size-4" />Overall</div>
            <div class="mt-2 flex items-center gap-2">
                <span class="h-3 w-3 rounded-full" [ngClass]="dotClass(overallStatus)" aria-hidden="true"></span>
                <span class="text-lg font-semibold text-gray-900 dark:text-white">{{ overallLabel }}</span>
            </div>
            <div *ngIf="health()?.generated_at" class="mt-1 text-xs text-gray-400">
                Refreshed {{ health()!.generated_at | date:'HH:mm:ss' }}{{ health()?.live ? ' · live probe' : '' }}
            </div>
        </div>
        <div class="panel rounded-2xl p-4">
            <div class="flex items-center gap-1.5 text-xs uppercase text-gray-400"><icon-server class="size-4" />Services</div>
            <ng-container *ngTemplateOutlet="counts; context: { c: serviceCounts }" />
        </div>
        <div class="panel rounded-2xl p-4">
            <div class="flex items-center gap-1.5 text-xs uppercase text-gray-400"><icon-users-group class="size-4" />Tenants</div>
            <ng-container *ngTemplateOutlet="counts; context: { c: tenantCounts }" />
        </div>
    </div>

    <!-- Services -->
    <div class="panel rounded-2xl p-0">
        <div class="flex items-center justify-between border-b border-gray-200 px-4 py-3 dark:border-white/10">
            <h5 class="flex items-center gap-2 text-base font-medium text-gray-900 dark:text-white"><icon-server class="size-5 text-gray-400" />Services</h5>
            <ng-container *ngTemplateOutlet="legend" />
        </div>
        <div class="overflow-x-auto">
            <table class="w-full text-sm">
                <thead>
                    <tr class="border-b border-gray-200 text-left text-xs uppercase text-gray-400 dark:border-white/10">
                        <th scope="col" class="px-4 py-3">Status</th>
                        <th scope="col" class="px-4 py-3">Target</th>
                        <th scope="col" class="px-4 py-3">Version</th>
                        <th scope="col" class="px-4 py-3"><span class="inline-flex items-center gap-1"><icon-clock class="size-3.5" />Latency</span></th>
                        <th scope="col" class="px-4 py-3"><span class="inline-flex items-center gap-1"><icon-trending-up class="size-3.5" />Uptime ({{ window() }})</span></th>
                        <th scope="col" class="px-4 py-3"><span class="inline-flex items-center gap-1"><icon-calendar class="size-3.5" />Last {{ stripDays }} days</span></th>
                        <th scope="col" class="px-4 py-3"><span class="sr-only">Checks</span></th>
                    </tr>
                </thead>
                <tbody>
                    <ng-container *ngFor="let target of health()?.services">
                        <ng-container *ngTemplateOutlet="targetRow; context: { target }" />
                    </ng-container>
                </tbody>
            </table>
        </div>
    </div>

    <!-- Tenants: searchable and filterable, because this list grows with the business. -->
    <div class="panel rounded-2xl p-0">
        <div class="flex flex-wrap items-center gap-3 border-b border-gray-200 px-4 py-3 dark:border-white/10">
            <h5 class="flex items-center gap-2 text-base font-medium text-gray-900 dark:text-white">
                <icon-users-group class="size-5 text-gray-400" />Tenants <span class="text-sm font-normal text-gray-400">({{ visibleTenants.length }} of {{ health()?.tenants?.length ?? 0 }})</span>
            </h5>
            <div class="ml-auto flex flex-wrap items-center gap-2">
                <div class="relative">
                    <icon-search class="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-gray-400" />
                    <input type="search" [(ngModel)]="tenantQuery" placeholder="Search tenant"
                        class="form-input h-9 w-48 rounded-full pl-9 text-sm" aria-label="Search tenant" />
                </div>
                <label class="flex cursor-pointer items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                    <input type="checkbox" class="form-checkbox" [(ngModel)]="problemsOnly" />
                    Problems only
                </label>
            </div>
        </div>
        <div class="overflow-x-auto">
            <table class="w-full text-sm">
                <thead>
                    <tr class="border-b border-gray-200 text-left text-xs uppercase text-gray-400 dark:border-white/10">
                        <th scope="col" class="px-4 py-3">Status</th>
                        <th scope="col" class="px-4 py-3">Target</th>
                        <th scope="col" class="px-4 py-3">Version</th>
                        <th scope="col" class="px-4 py-3"><span class="inline-flex items-center gap-1"><icon-clock class="size-3.5" />Latency</span></th>
                        <th scope="col" class="px-4 py-3"><span class="inline-flex items-center gap-1"><icon-trending-up class="size-3.5" />Uptime ({{ window() }})</span></th>
                        <th scope="col" class="px-4 py-3"><span class="inline-flex items-center gap-1"><icon-calendar class="size-3.5" />Last {{ stripDays }} days</span></th>
                        <th scope="col" class="px-4 py-3"><span class="sr-only">Checks</span></th>
                    </tr>
                </thead>
                <tbody>
                    <ng-container *ngFor="let target of visibleTenants">
                        <ng-container *ngTemplateOutlet="targetRow; context: { target }" />
                    </ng-container>
                    <tr *ngIf="!visibleTenants.length">
                        <td colspan="7" class="px-4 py-6 text-center text-sm text-gray-400">
                            {{ health()?.tenants?.length ? 'No tenants match.' : 'No tenants sampled yet.' }}
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</div>

<ng-template #counts let-c="c">
    <div class="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm">
        <span class="font-semibold text-emerald-600">{{ c.ok }} ok</span>
        <span [class.font-semibold]="c.degraded" [ngClass]="c.degraded ? 'text-amber-600' : 'text-gray-400'">{{ c.degraded }} degraded</span>
        <span [class.font-semibold]="c.down" [ngClass]="c.down ? 'text-red-600' : 'text-gray-400'">{{ c.down }} down</span>
        <span *ngIf="c.unknown" class="text-gray-400">{{ c.unknown }} no data</span>
    </div>
</ng-template>

<ng-template #legend>
    <div class="flex items-center gap-3 text-xs text-gray-400">
        <span class="flex items-center gap-1"><span class="h-2 w-2 rounded-sm bg-emerald-500"></span>ok</span>
        <span class="flex items-center gap-1"><span class="h-2 w-2 rounded-sm bg-amber-500"></span>degraded</span>
        <span class="flex items-center gap-1"><span class="h-2 w-2 rounded-sm bg-red-500"></span>down</span>
        <span class="flex items-center gap-1"><span class="h-2 w-2 rounded-sm bg-gray-300 dark:bg-gray-600"></span>no data</span>
    </div>
</ng-template>

<!-- Shared row for services and tenants. (The header row is inlined in each table above rather than
     templated, so Sonar's static HTML check can see the <th>s.) -->
<ng-template #targetRow let-target="target">
    <!-- The row itself is not interactive (Sonar S6819): the real <button> in the last cell toggles the checks. -->
    <tr class="border-b border-gray-100 hover:bg-gray-50 dark:border-white/5 dark:hover:bg-white/5">
        <td class="px-4 py-3">
            <span class="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium" [ngClass]="pillClass(target.status)">
                <span class="h-1.5 w-1.5 rounded-full" [ngClass]="dotClass(target.status)" aria-hidden="true"></span>
                {{ target.status }}
            </span>
        </td>
        <td class="px-4 py-3">
            <div class="font-medium text-gray-700 dark:text-gray-200">{{ target.target_key }}</div>
            <div *ngIf="failingChecks(target).length" class="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-red-500">
                failing:
                <span *ngFor="let name of failingChecks(target)" class="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 dark:bg-red-500/10">
                    <app-check-icon [name]="name" class="size-3.5" />{{ name }}
                </span>
            </div>
        </td>
        <td class="px-4 py-3 text-xs text-gray-400">
            <ng-container *ngIf="target.target_kind === 'SERVICE'; else tenantKind">{{ target.version ?? '—' }}&#64;{{ target.git_sha ?? '—' }}</ng-container>
            <ng-template #tenantKind>tenant</ng-template>
        </td>
        <td class="px-4 py-3 text-xs text-gray-400">{{ target.latency_ms ?? '—' }}ms</td>
        <td class="px-4 py-3 text-xs font-medium" [ngClass]="uptimeClass(targetKey(target))">{{ uptimeText(targetKey(target)) }}</td>
        <td class="px-4 py-3">
            <app-status-strip [points]="historyOf(targetKey(target))" [days]="stripDays" />
        </td>
        <td class="px-4 py-3 text-xs">
            <button type="button" class="inline-flex items-center gap-1 rounded-full px-2 py-1 text-gray-500 hover:bg-gray-100 hover:text-gray-800 dark:hover:bg-white/10 dark:hover:text-white"
                [attr.aria-expanded]="isExpanded(targetKey(target))" (click)="toggle(targetKey(target))">
                <icon-list-check class="size-4" />{{ isExpanded(targetKey(target)) ? 'Hide checks' : 'Checks' }}
            </button>
        </td>
    </tr>
    <tr *ngIf="isExpanded(targetKey(target))" class="border-b border-gray-100 bg-gray-50 dark:border-white/5 dark:bg-white/5">
        <td colspan="7" class="px-4 py-3">
            <div class="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
                <div *ngFor="let check of checksOf(target)"
                    class="rounded-xl border border-gray-200 bg-white px-3 py-2 dark:border-white/10 dark:bg-[#0e1726]">
                    <div class="flex items-center justify-between gap-2">
                        <span class="flex items-center gap-1.5 text-sm font-medium text-gray-700 dark:text-gray-200">
                            <app-check-icon [name]="check.name" class="size-4 text-gray-400" />{{ check.name }}
                        </span>
                        <span class="rounded-full px-2 py-0.5 text-xs font-medium"
                            [ngClass]="check.status === 'ok' ? pillClass('ok') : pillClass('down')">{{ check.status }}</span>
                    </div>
                    <div class="mt-1 text-xs text-gray-400">
                        {{ check.required ? 'required' : 'optional' }} · {{ check.latency_ms }}ms
                        <span *ngIf="check.detail"> · {{ check.detail }}</span>
                    </div>
                </div>
            </div>
            <p *ngIf="target.sampled_at" class="mt-2 text-xs text-gray-400">Sampled {{ target.sampled_at | date:'medium' }}</p>
        </td>
    </tr>
</ng-template>
